/**
 * Campaign archive — filters, query building and import-job state.
 *
 * Lifted out of the nine `_arch*` globals and loadArchive in
 * public/index.html. The archive is the all-time history built by the nightly
 * snapshot job, so it includes accounts that have since been archived or banned
 * — that is the point of it, and why it is a separate surface from Campaigns.
 */

import { ARCH_PAGE_SIZE, DEBOUNCED_FILTER_KEYS, FILTER_KEYS } from 'constants/archive.mjs';

/** The default state — everything off, newest spend first. */
export const emptyArchiveFilters = () => ({
  q: '', account: '', archived: '', minSpend: '', maxSpend: '',
  minRoas: '', maxRoas: '', minCpa: '', maxCpa: '', dateFrom: '', dateTo: '',
  view: 'campaigns', sort: 'spend', dir: 'desc', page: 0,
});

/** Read the filter state out of URL search params. */
export function parseArchiveFilters(params) {
  const get = k => params?.get?.(k) ?? '';
  const filters = emptyArchiveFilters();
  for (const k of FILTER_KEYS) filters[k] = get(k);
  filters.view = get('view') === 'ads' ? 'ads' : 'campaigns';
  filters.sort = get('sort') || 'spend';
  filters.dir = get('dir') === 'asc' ? 'asc' : 'desc';
  filters.page = Math.max(0, parseInt(get('page'), 10) || 0);
  return filters;
}

/**
 * The query string for /api/archive/search.
 *
 * Empty filters are omitted rather than sent blank — the server treats a present
 * key as a real constraint, so `minSpend=` would filter on zero.
 */
export function archiveQuery(filters) {
  const params = new URLSearchParams({
    sort: filters.sort || 'spend',
    dir: filters.dir || 'desc',
    page: String(filters.page ?? 0),
  });
  for (const k of FILTER_KEYS) {
    const v = filters[k];
    if (v !== '' && v != null) params.set(k, String(v));
  }
  return params.toString();
}

/** The typed-into values, lifted out of a filter set. */
export function draftsOf(filters) {
  const drafts = {};
  for (const k of DEBOUNCED_FILTER_KEYS) drafts[k] = filters?.[k] ?? '';
  return drafts;
}

/**
 * What to send once typing settles — the drafts that no longer match the URL,
 * or null when there is nothing to do.
 *
 * The null matters: this runs on a timer after every keystroke AND after every
 * URL change, so most calls have nothing to say. Returning a patch anyway would
 * rewrite the URL with the values already in it, and each rewrite re-runs the
 * search that just finished, forever.
 */
export function debouncedPatch(drafts, filters) {
  const patch = {};
  for (const k of DEBOUNCED_FILTER_KEYS) {
    if ((drafts?.[k] ?? '') !== (filters?.[k] ?? '')) patch[k] = drafts?.[k] ?? '';
  }
  if (!Object.keys(patch).length) return null;
  // Narrowing the set invalidates whatever page you were on — page 9 of the old
  // results is usually past the end of the new ones.
  patch.page = 0;
  return patch;
}

/**
 * Pull the boxes back into line with the URL when the URL changed for a reason
 * that was NOT this debounce — Reset, Back/Forward, or a pasted link.
 *
 * Three things have to be true before a box is overwritten, and each one is a
 * way this goes wrong if you leave it out:
 *
 *   the URL value MOVED      Sorting, paging and picking an account all rewrite
 *                            the URL without touching a single bound. Judged on
 *                            "does the box differ from the URL" instead, every
 *                            one of those clicks would wipe a number you had
 *                            typed but not yet paused on — typed input lost
 *                            silently, one click away, with no error.
 *
 *   it differs from the box   Nothing to do otherwise.
 *
 *   we did not just send it   `pushed` is what the debounce last wrote. The URL
 *                            catches up half a second late: type "50", pause,
 *                            the search fires, then type "0" — and the "50"
 *                            landing back would overwrite the "500" now in the
 *                            box, eating the character and jumping the caret.
 *
 * Returns null when nothing needs to move, so React can skip the render.
 */
export function resyncDrafts(drafts, filters, prevFilters, pushed = {}) {
  let next = null;
  for (const k of DEBOUNCED_FILTER_KEYS) {
    const fromUrl = filters?.[k] ?? '';
    if (fromUrl === (prevFilters?.[k] ?? '')) continue;  // this bound did not move
    if (fromUrl === (drafts?.[k] ?? '')) continue;       // already in step
    if (fromUrl === pushed[k]) continue;                 // our own write, landing late
    next ??= { ...drafts };
    next[k] = fromUrl;
  }
  return next;
}

/** Which endpoint a view reads from. */
export const archiveEndpoint = (view, detailId) => {
  if (detailId) {
    return `${view === 'ads' ? '/api/archive/ads/' : '/api/archive/'}${encodeURIComponent(detailId)}`;
  }
  return view === 'ads' ? '/api/archive/ads/search' : '/api/archive/search';
};

/** True when anything is narrowing the list — drives the "Reset" affordance. */
export function hasActiveFilters(filters) {
  return FILTER_KEYS.some(k => filters[k] !== '' && filters[k] != null);
}

/**
 * Cost per purchase for a row or a totals block — spend ÷ purchases, or NULL
 * when there is nothing to divide by.
 *
 * The null is the whole point, and it is not the same call the ROAS cell makes
 * one column to the left. A campaign with no spend genuinely has a ROAS of 0:
 * it earned nothing per dollar. A campaign with no purchases does not have a CPA
 * of $0 — it has no CPA at all, and calling it $0 makes the money-burners look
 * like the cheapest buys on the page, which is the reading the whole column
 * exists to give. Whatever renders this has to say "no purchases", not "$0.00".
 *
 * Reads the server's own `cpa` when it sent one, so a row and its sort order can
 * never disagree, and falls back to the same arithmetic for a payload that
 * predates it.
 */
export function rowCpa(row) {
  if (!row) return null;
  if (row.cpa !== undefined) return row.cpa == null ? null : Number(row.cpa);
  const spend = parseFloat(row.totalSpend ?? row.spend) || 0;
  const purchases = parseInt(row.totalPurchases ?? row.purchases, 10) || 0;
  return purchases > 0 ? spend / purchases : null;
}

/** A CPA as money, or the em dash a row with no purchases has earned. */
export function fmtCpa(cpa) {
  if (cpa == null || !Number.isFinite(Number(cpa))) return '—';
  return `$${Number(cpa).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Status → the badge variant the stylesheet already defines. */
export function badgeKind(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'ACTIVE') return 'active';
  if (s === 'PAUSED') return 'paused';
  if (s === 'ARCHIVED' || s === 'DELETED') return 'archived';
  return 'paused';
}

/** ISO date N days back, for the import panel's range shortcuts. */
export function daysAgo(n, now = Date.now()) {
  return new Date(now - n * 86400000).toISOString().split('T')[0];
}

/** "~4 min remaining" / "~40s remaining" — the original's two thresholds. */
export function importEta(seconds) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return '';
  const s = Math.max(0, Math.round(Number(seconds)));
  return s > 90 ? `~${Math.round(s / 60)} min remaining` : `~${s}s remaining`;
}

/**
 * What the import panel should show.
 *
 * The job runs on the SERVER — months of history take far longer than a browser
 * will wait — so the panel only starts it and polls. Closing the tab, switching
 * theme or logging out does not stop it, and `polling` is what tells the view to
 * keep asking.
 *
 * The shape read here is historyImport.status() plus lastRun(), as served by
 * GET /api/archive/import/status:
 *
 *   { running, everRun, from, to, total, done, percent, captured, currentDate,
 *     currentAccount, cancelled, error, etaSeconds,
 *     failures: [{ accountId, accountName, reason }],
 *     lastRun: { from, to, captured, cancelled, finishedAt, failures } | null }
 *
 * `error` and `cancelled` describe how the LAST RUN ended — they are not the
 * panel being unavailable, so a failed import still shows its form. Only a
 * payload with no job fields at all (a transport error) counts as unavailable.
 *
 * `everRun` distinguishes "finished during this session", which the panel
 * reports in full, from `lastRun`, the durable record that survives a restart.
 * Both can be on screen at once, as they were in the original.
 */
export function importState(status) {
  if (!status || (status.error && status.everRun === undefined && !status.running)) {
    return { phase: 'unavailable', polling: false, error: status?.error ?? null, last: null };
  }

  const num = v => Number(v ?? 0) || 0;
  const failuresOf = f => (Array.isArray(f) ? f : []);
  const last = status.lastRun
    ? {
      from: status.lastRun.from ?? '',
      to: status.lastRun.to ?? '',
      captured: num(status.lastRun.captured),
      cancelled: Boolean(status.lastRun.cancelled),
      finishedAt: status.lastRun.finishedAt ?? '',
      failures: failuresOf(status.lastRun.failures),
    }
    : null;

  if (status.running) {
    const done = num(status.done);
    const total = num(status.total);
    return {
      phase: 'running',
      polling: true,
      from: status.from ?? '',
      to: status.to ?? '',
      done,
      total,
      // The server already computes this; falling back keeps a 0 total from
      // dividing rather than reaching for NaN.
      pct: Math.max(0, Math.min(100, status.percent != null
        ? num(status.percent)
        : (total > 0 ? Math.round((done / total) * 100) : 0))),
      captured: num(status.captured),
      currentDate: status.currentDate ?? '',
      currentAccount: status.currentAccount ?? '',
      eta: importEta(status.etaSeconds),
      failures: failuresOf(status.failures),
      last,
    };
  }

  return {
    phase: 'idle',
    polling: false,
    // Present only when a run finished in THIS session, which is what lets the
    // panel say "✅ Finished" once rather than on every later visit.
    finished: status.everRun
      ? {
        captured: num(status.captured),
        cancelled: Boolean(status.cancelled),
        error: status.error ?? null,
        failures: failuresOf(status.failures),
      }
      : null,
    last,
  };
}

/**
 * Page arithmetic for the archive list.
 *
 * `size` should be the page size the SERVER reported for this very response
 * (`filters.pageSize`), not an assumption — the pager's job is to describe the
 * rows that actually arrived. Guessing a bigger number than the server uses
 * makes the pager both under-count the pages and over-state the range, which
 * strands every row past the last page it offers.
 *
 * A missing or nonsense size falls back to ARCH_PAGE_SIZE rather than dividing
 * by zero or NaN.
 */
export function archivePages(total, page, size = ARCH_PAGE_SIZE) {
  const n = Number(size);
  const safeSize = Number.isFinite(n) && n >= 1 ? Math.floor(n) : ARCH_PAGE_SIZE;
  const safeTotal = Math.max(0, Number(total) || 0);
  const totalPages = Math.max(1, Math.ceil(safeTotal / safeSize));
  const safe = Math.max(0, Math.min(page, totalPages - 1));
  return {
    page: safe,
    totalPages,
    total: safeTotal,
    size: safeSize,
    hasPrev: safe > 0,
    hasNext: safe < totalPages - 1,
    from: safeTotal === 0 ? 0 : safe * safeSize + 1,
    to: Math.min(safeTotal, (safe + 1) * safeSize),
  };
}
